import type { CSSProperties, ReactNode } from 'react';

import { COVER_NONE, type Cover } from '@/types';

/**
 * 封面呈現層 — projects / brand_kits 共用的三態渲染(R245;BE docs/92)
 * -----------------------------------------------------------------------------
 * 全站唯一一處把 `Cover` 變成畫面的地方。四個呼叫端(專案卡 / 專案列 / 首頁近期
 * 專案 / 品牌包卡)共用它,是為了讓「三態 → 畫面」只有一份判準 —— 這個域**已經
 * 有過兩套獨立的封面推導**(`projectCovers.pickCoverGradient` 與
 * `homeMeta.projectCoverStyle`),再多幾份必然漂移。
 *
 * fallback 順序(BE docs/92 §4 建議):
 *   image → `<img>`;color → 純色;none → 呼叫端給的既有漸層
 *
 * ⚠️ **`kind` 是唯一判準**,不看 `color` / `image` 誰有值 —— 三態互斥由 BE
 * validator 保證,FE 的 `mapCoverDto` 也已經把不合法值降級成 `none`,所以到這層
 * 的值一定是乾淨的。
 *
 * 構圖是前端的事:後端只把長邊縮到 480 且**不裁切**,所以固定比例框由這裡用
 * `object-fit: cover` 自己決定怎麼裁。
 */

interface CoverSurfaceProps {
  cover: Cover;
  /**
   * `kind === 'none'` 時的底 —— 傳既有的品牌色漸層進來。
   *
   * 也當作圖片的襯底:`<img>` 載不出來(data URI 理論上不會,但瀏覽器記憶體吃緊
   * 時會)時看到的是漸層而不是破圖框。
   */
  fallback: string;
  /** 純裝飾傳 `''`(等同 `aria-hidden`);有語意時給文字。 */
  alt?: string;
  style?: CSSProperties;
  /** 疊在封面上的東西(例:專案卡右上的 kebab 選單)。 */
  children?: ReactNode;
  'data-testid'?: string;
  /**
   * 圖片的鋪法(R366 opt-in):
   * - `'crop'`(預設)— `width/height: 100%` + `object-fit: cover`:固定比例框
   *   (專案卡 / 專案列 / 首頁近期專案 / 品牌包卡)自行決定裁切構圖。
   * - `'natural'` — 寬滿版、高依原圖比例(`height: auto`),再以
   *   `naturalHeightRange` clamp;貼到界時 `object-fit: contain` 完整顯示、露
   *   fallback 當襯底。給「上傳後要能對照原圖」的表單預覽用 —— 預覽的壞法只能
   *   是留邊,**不可以是裁切**(R347 範本縮圖同族判準)。
   *   ⚠️ 走這個模式的呼叫端不要再給固定 `height` / `aspectRatio`:容器高度由
   *   縮放後的圖片決定,兩邊都給就是兩份會漂移的版面事實。
   */
  imageFit?: 'crop' | 'natural';
  /** `imageFit='natural'` 時的高度 clamp(px)。 */
  naturalHeightRange?: { min: number; max: number };
}

export function CoverSurface({
  cover,
  fallback,
  alt = '',
  style,
  children,
  'data-testid': testId,
  imageFit = 'crop',
  naturalHeightRange,
}: CoverSurfaceProps) {
  // 防禦性正規化:封面是裝飾元素,不值得為了它讓整頁 render throw
  // (R224 補 error boundary 的理由同型)。型別上 `cover` 是必填,這裡擋的是
  // 測試 fixture / 存量呼叫端漏傳的情況 —— 漏傳就當 `none`,畫面照樣正常。
  // 收成一個具名值(而不是只取 `kind`)才能讓 TS 對 discriminated union 做收窄。
  const safe: Cover = cover ?? COVER_NONE;

  return (
    <div
      data-testid={testId}
      data-cover-kind={safe.kind}
      style={{
        position: 'relative',
        overflow: 'hidden',
        // color 用實色;image 也鋪 fallback 當襯底;none 就是 fallback 本身
        background: safe.kind === 'color' ? safe.color : fallback,
        ...style,
      }}
    >
      {safe.kind === 'image' && (
        <img
          src={safe.image}
          alt={alt}
          // 後端不裁切(長邊 480 保持原比例)→ 構圖在這裡決定:crop 給固定比例
          // 框裁切;natural 依原圖比例撐高(clamp 到界時 contain,只留邊不裁)
          style={
            imageFit === 'natural'
              ? {
                  display: 'block',
                  width: '100%',
                  height: 'auto',
                  minHeight: naturalHeightRange?.min,
                  maxHeight: naturalHeightRange?.max,
                  objectFit: 'contain',
                }
              : {
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }
          }
        />
      )}
      {children}
    </div>
  );
}
